import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  SearchIcon,
  MapPinIcon,
  StarIcon,
  UserIcon,
  ClockIcon,
  AwardIcon,
  FilterIcon
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { doctorService } from "../../services";
import Breadcrumb from "../Breadcrumb";
import { mockData } from "@/data/mock";

const FindDoctors = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState("all-cities");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all-specialties");
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch doctors from API
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        const doctorsData = await doctorService.getAllDoctors();
        
        // Handle API response structure (prefer processed { data })
        let doctorsArray = [];
        if (Array.isArray(doctorsData?.data)) {
          doctorsArray = doctorsData.data;
        } else if (Array.isArray(doctorsData?.result?.doctors)) {
          doctorsArray = doctorsData.result.doctors;
        } else if (Array.isArray(doctorsData?.docs)) {
          doctorsArray = doctorsData.docs;
        } else if (Array.isArray(doctorsData)) {
          doctorsArray = doctorsData;
        }
        
        console.log('Processed doctors data:', doctorsArray);
        setDoctors(doctorsArray);
      } catch (err) {
        console.error('Error fetching doctors:', err);
        setError('Failed to load doctors');
        // Fallback to mock data
        setDoctors([
    {
      id: 1,
      name: "Dr. Rajesh Kumar",
      specialty: "General Surgery",
      experience: "15+ years",
      rating: 4.8,
      reviews: 234,
      city: "Delhi",
      hospital: "TheLivCure Elantis",
      education: "MBBS, MS (General Surgery)",
      languages: ["English", "Hindi"],
      consultationFee: 800,
      availableToday: true,
      image: "doctor1"
    },
    {
      id: 2,
      name: "Dr. Priya Sharma",
      specialty: "Gynaecology",
      experience: "12+ years",
      rating: 4.9,
      reviews: 189,
      city: "Mumbai",
      hospital: "TheLivCure Mumbai Central",
      education: "MBBS, MD (Gynaecology)",
      languages: ["English", "Hindi", "Marathi"],
      consultationFee: 900,
      availableToday: true,
      image: "doctor2"
    },
    {
      id: 3,
      name: "Dr. Amit Singh",
      specialty: "Urology",
      experience: "18+ years",
      rating: 4.7,
      reviews: 156,
      city: "Bangalore",
      hospital: "TheLivCure Bangalore",
      education: "MBBS, MS (Urology)",
      languages: ["English", "Hindi", "Kannada"],
      consultationFee: 750,
      availableToday: false,
      image: "doctor3"
    },
    {
      id: 4,
      name: "Dr. Sunita Patel",
      specialty: "Ophthalmology",
      experience: "10+ years",
      rating: 4.9,
      reviews: 298,
      city: "Ahmedabad",
      hospital: "TheLivCure Ahmedabad",
      education: "MBBS, MS (Ophthalmology)",
      languages: ["English", "Hindi", "Gujarati"],
      consultationFee: 700,
      availableToday: true,
      image: "doctor4"
    },
    {
      id: 5,
      name: "Dr. Vikram Reddy",
      specialty: "Orthopedics",
      experience: "20+ years",
      rating: 4.8,
      reviews: 167,
      city: "Hyderabad",
      hospital: "TheLivCure Hyderabad",
      education: "MBBS, MS (Orthopedics)",
      languages: ["English", "Hindi", "Telugu"],
      consultationFee: 850,
      availableToday: true,
      image: "doctor5"
    },
    {
      id: 6,
      name: "Dr. Meera Joshi",
      specialty: "ENT (Ear, Nose, Throat)",
      experience: "14+ years",
      rating: 4.7,
      reviews: 203,
      city: "Pune",
      hospital: "TheLivCure Pune",
      education: "MBBS, MS (ENT)",
      languages: ["English", "Hindi", "Marathi"],
      consultationFee: 650,
      availableToday: true,
      image: "doctor6"
    }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctors();
  }, []);

  const filteredDoctors = Array.isArray(doctors) ? doctors.filter(doctor => {
    return (
      (searchTerm === "" ||
        (doctor.name || doctor.fullName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doctor.specialty || "").toLowerCase().includes(searchTerm.toLowerCase())) &&
      (selectedCity === "all-cities" || selectedCity === "" || 
        (doctor.city || doctor.cities?.[0] || "").includes(selectedCity)) &&
      (selectedSpecialty === "all-specialties" || selectedSpecialty === "" || 
        (doctor.specialty || "").includes(selectedSpecialty))
    );
  }) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading doctors...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { label: "Find Doctors" }
            ]} 
          />
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Find the <span className="text-blue-600">Best Doctors</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with experienced medical professionals across India for expert consultation and treatment
            </p>
          </div>

          {/* Search Filters */}
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Search doctors, specialties..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-cities">All Cities</SelectItem>
                    <SelectItem value="Faridabad">Faridabad</SelectItem>
                    <SelectItem value="Delhi">Delhi</SelectItem>
                    <SelectItem value="Gurgaon">Gurgaon</SelectItem>
                    <SelectItem value="Noida">Noida</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-specialties">All Specialties</SelectItem>
                    {mockData.specialties.map(specialty => (
                      <SelectItem key={specialty.name} value={specialty.name}>
                        {specialty.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button className="bg-blue-600 hover:bg-blue-700 sm:col-span-2 lg:col-span-1">
                  <SearchIcon className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Doctors List */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {filteredDoctors.length} Doctors Found
              </h2>
              <p className="text-gray-600">Based on your search criteria</p>
            </div>

            <Button variant="outline">
              <FilterIcon className="w-4 h-4 mr-2" />
              More Filters
            </Button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
            {filteredDoctors.map((doctor) => (
              <Card key={doctor._id || doctor.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    {/* Doctor Image */}
                    {doctor.profileImageUrl ? (
                      <img
                        src={doctor.profileImageUrl}
                        alt={doctor.fullName || doctor.name}
                        className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                      </div>
                    )}

                    {/* Doctor Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900">{doctor.fullName || doctor.name}</h3>
                          <p className="text-blue-600 font-medium text-sm sm:text-base">{doctor.specialty}</p>
                          <p className="text-xs sm:text-sm text-gray-600">{doctor.qualifications?.map(q => q.degree).join(", ") || doctor.education || "Not specified"}</p>
                        </div>

                        {(doctor.availableToday || doctor.status === 'active') && (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            Available Today
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 my-3 sm:my-4">
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <AwardIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          {doctor.experience} years
                        </div>
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <MapPinIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          {doctor.cities?.[0] || doctor.city || "Not specified"}
                        </div>
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 fill-yellow-400 text-yellow-400" />
                          {doctor.rating || 0} ({doctor.reviewCount || doctor.reviews || 0} reviews)
                        </div>
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          ₹{doctor.consultationFees || doctor.consultationFee || 500} consultation
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="text-xs sm:text-sm text-gray-600">
                          <span className="font-medium">Languages:</span> {Array.isArray(doctor.languages) ? doctor.languages.join(", ") : "English, Hindi"}
                        </div>

                        <div className="flex flex-col xs:flex-row gap-2">
                          <Link to={`/doctor/${doctor._id || doctor.id}`}>
                            <Button variant="outline" size="sm" className="w-full xs:w-auto text-xs">
                              View Profile
                            </Button>
                          </Link>
                          <Button size="sm" className="bg-gray-900 hover:bg-gray-600 w-full xs:w-auto text-xs">
                            Book Appointment
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* No Results */}
          {filteredDoctors.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No doctors found</h3>
              <p className="text-gray-600">Try adjusting your search criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Can't Find the Right Doctor?</h2>
          <p className="text-lg mb-6 opacity-90">
            Our medical coordinators will help you find the perfect specialist for your needs
          </p>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
            Get Personalized Recommendations
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FindDoctors;